import { INSTAGRAM_URL, FACEBOOK_URL } from "../../constants/socialLinks";
import React, { useState } from "react";
import "./Contact.css";
import Header from "../../components/Header/Header";
import Footer from "../../components/Footer/Footer";
import { submitContactForm } from "../../api/contactApi";
import { Helmet } from "react-helmet-async";
import {
  FaPhoneAlt,
  FaEnvelope,
  FaMapMarkerAlt,
  FaInstagram,
  FaFacebookF,
} from "react-icons/fa";
import {
  GiMeditation,
  GiCrystalCluster,
  GiPrayerBeads,
  GiCrystalBars,
  GiBurningRoundShot,
} from "react-icons/gi";

import { PiDiamondsFourFill } from "react-icons/pi";
const CONTACT_CATEGORIES = [
  "Rudrakshas",
  "Shivling",
  "Shankh & Sea Shells",
  "Shaligram",
  "Gemstones",
  "Crystals & Healing Stones",
  "Vastu Shashtra Products",
  "Pooja Essentials",
  "Tulsi, Chandan & Other Wood Products",
  "Perfumes & Fragrances",
  "Shop by Concern",
  "Shop by Astrology",
  "Shop by Birth Number",
  "All Astrology Services We Are Providing",
  "All Numerology Services We Are Providing",
  "All Vastu Services We Are Providing",
];

function Contact() {
  const [form, setForm] = useState({
    name: "",
    age: "",
    phone: "",
    email: "",
    category: "",
    message: "",
  });
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [feedback, setFeedback] = useState("");

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (
      !form.name.trim() ||
      !form.phone.trim() ||
      !form.email.trim() ||
      !form.message.trim()
    ) {
      setStatus("error");
      setFeedback("Please fill in your name, phone, email, and message.");
      return;
    }

    setStatus("loading");
    setFeedback("");

    try {
      const payload = {
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
        message: form.message.trim(),
        ...(form.age ? { age: Number(form.age) } : {}),
        ...(form.category ? { category: form.category } : {}),
      };

      const result = await submitContactForm(payload);

      if (!result.ok) {
        throw new Error(
          result.message || "Something went wrong. Please try again.",
        );
      }

      setStatus("success");
      setFeedback(
        "Thank you! Your message has been submitted. We'll get back to you soon.",
      );
      setForm({
        name: "",
        age: "",
        phone: "",
        email: "",
        category: "",
        message: "",
      });
    } catch (err) {
      setStatus("error");
      setFeedback(err.message || "Something went wrong. Please try again.");
    }
  };

  const services = [
    {
      icon: <GiMeditation />,
      title: "Vedic Consultations",
      desc: "Personalized guidance based on Vedic wisdom.",
    },
    {
      icon: <PiDiamondsFourFill />,
      title: "Gemstones",
      desc: "Certified gemstones for positive energy and balance.",
    },
    {
      icon: <GiPrayerBeads />,
      title: "Rudrakshas",
      desc: "Authentic Rudrakshas for spiritual growth and protection.",
    },
    {
      icon: <GiCrystalCluster />,
      title: "Healing Crystals",
      desc: "Handpicked crystals to heal, protect & energize.",
    },
    {
      icon: <GiBurningRoundShot />,
      title: "Pooja Essentials",
      desc: "Everything you need for spiritual rituals and pujas.",
    },
    {
      icon: <GiCrystalBars />,
      title: "Vastu Solutions",
      desc: "Enhance harmony and prosperity with Vastu remedies.",
    },
  ];

  return (
    <>
      <Helmet>
        {/* Basic SEO */}
        <title>
          Contact Astro Logical Solutions | Astrology & Vastu Experts
        </title>

        <meta
          name="description"
          content="Contact Astro Logical Solutions for astrology, Vastu, numerology, Rudraksha and spiritual guidance. Get expert assistance and answers to your queries."
        />

        <link rel="canonical" href="https://astrologicalsolutions.in/contact" />

        <meta name="robots" content="index, follow, max-image-preview:large" />

        {/* Open Graph SEO */}
        <meta property="og:type" content="website" />

        <meta
          property="og:title"
          content="Contact Astro Logical Solutions | Astrology & Vastu Experts"
        />

        <meta
          property="og:description"
          content="Contact Astro Logical Solutions for astrology, Vastu, numerology, Rudraksha and spiritual guidance."
        />

        <meta
          property="og:url"
          content="https://astrologicalsolutions.in/contact"
        />

        <meta property="og:site_name" content="Astro Logical Solutions" />

        <meta
          property="og:image"
          content="https://astrologicalsolutions.in/og/contact-banner.jpg"
        />

        <meta
          property="og:image:alt"
          content="Contact Astro Logical Solutions for astrology and Vastu guidance"
        />

        <meta property="og:locale" content="en_IN" />

        {/* Twitter Card SEO */}
        <meta name="twitter:card" content="summary_large_image" />

        <meta
          name="twitter:title"
          content="Contact Astro Logical Solutions | Astrology & Vastu Experts"
        />

        <meta
          name="twitter:description"
          content="Contact Astro Logical Solutions for astrology, Vastu, numerology, Rudraksha and spiritual guidance."
        />

        <meta
          name="twitter:image"
          content="https://astrologicalsolutions.in/og/contact-banner.jpg"
        />

        {/* Contact Page Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ContactPage",
            name: "Contact Astro Logical Solutions",
            url: "https://astrologicalsolutions.in/contact",
            description:
              "Contact Astro Logical Solutions for astrology, Vastu, numerology, Rudraksha and spiritual guidance.",
            isPartOf: {
              "@type": "WebSite",
              name: "Astro Logical Solutions",
              url: "https://astrologicalsolutions.in/",
            },
            about: {
              "@type": "Organization",
              name: "Astro Logical Solutions",
              url: "https://astrologicalsolutions.in/",
            },
          })}
        </script>
      </Helmet>
      <Header />
      <section className="contact-banner">
        <div className="banner-overlay"></div>
      </section>
      <section className="offer-section">
        <span className="offer-small-title">What We Offer</span>
        <h1 className="offer-title">
          Holistic Solutions for Your Well-being
          <div className="about-heading-line">
            <img
              src="https://res.cloudinary.com/dbb2hkjri/image/upload/v1786441287/1684481897-flower-divider-horizontal-orange_f2omyx.svg"
              alt="Heading decoration"
            />
          </div>
        </h1>
        <div className="offer-grid">
          {services.map((item, index) => (
            <div className="offer-cardg" key={index}>
              <div className="offer-icon">{item.icon}</div>

              <h3>{item.title}</h3>

              <p>{item.desc}</p>
            </div>
          ))}
        </div>
      </section>
      <section className="contact-section">
        <div className="contact-container">
          <div className="contact-left">
            <span className="contact-subtitle">Get In Touch</span>
            <h2>Speak to Our Experts</h2>
            <p>
              Connect with our team of experts for personalized guidance and
              support on your wellness journey.
            </p>
            <div className="contact-info-box">
              {/* <div className="icon">
                  <FaPhoneAlt />
                </div> */}
              {/* <div>
                  <h4>Call Us</h4>

                  <a
                    href="https://api.whatsapp.com/send/?phone=917351519573&text=Hello%20Astro%20Logical%20Solutions%2C%20I%20would%20like%20some%20guidance.&type=phone_number&app_absent=0"
                    target="_blank"
                    className="num"
                    rel="noopener noreferrer"
                  >
                    7351519573
                  </a>
                  <a
                    href="https://api.whatsapp.com/send/?phone=917351739191&text=Hello+Astro+Logical+Solutions%2C+I+would+like+some+guidance.&type=phone_number&app_absent=0"
                    target="_blank"
                    className="num"
                    rel="noopener noreferrer"
                  >
                    7351739191
                  </a>
                </div> */}

              <div className="info-item">
                <div className="icon">
                  <FaMapMarkerAlt />
                </div>

                <div>
                  <h4>Address</h4>
                  <span>
                    Shiva Aangan, Bachan Singh Colony, Lane No 9, Muzaffarnagar,
                    Uttar Pradesh, 251001
                  </span>
                </div>
              </div>
              <div className="info-item">
                <div className="icon">
                  <FaEnvelope />
                </div>

                <div>
                  <h4>Mail Us</h4>
                  <span>
                    <a
                      className="number"
                      href="mailto:support@astrologicalsolutions.in"
                    >
                      support@astrologicalsolutions.in
                    </a>
                  </span>
                </div>
              </div>
            </div>

            <div className="social-section">
              <span>Follow Us On:</span>

              <div className="social-icons">
                <a
                  href={INSTAGRAM_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Instagram"
                >
                  <FaInstagram />
                </a>

                <a
                  href={FACEBOOK_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Facebook"
                >
                  <FaFacebookF />
                </a>
              </div>
            </div>
          </div>
          <div className="contact-right">
            <h3>Schedule Your Consultation</h3>

            {feedback && (
              <div
                className={
                  status === "success"
                    ? "contact-form-success"
                    : "contact-form-error"
                }
              >
                {feedback}
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div className="form-grid">
                <input
                  type="text"
                  name="name"
                  placeholder="Your Name"
                  value={form.name}
                  onChange={handleChange}
                />

                <input
                  type="number"
                  name="age"
                  placeholder="Age"
                  value={form.age}
                  onChange={handleChange}
                  min="1"
                />

                <input
                  type="text"
                  name="phone"
                  placeholder="Phone Number"
                  value={form.phone}
                  onChange={handleChange}
                />

                <input
                  type="email"
                  name="email"
                  placeholder="Email Address"
                  value={form.email}
                  onChange={handleChange}
                />
              </div>

              <select
                name="category"
                value={form.category}
                onChange={handleChange}
              >
                <option value="">Category</option>
                {CONTACT_CATEGORIES.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>

              <textarea
                rows="6"
                name="message"
                placeholder="Message"
                value={form.message}
                onChange={handleChange}
              ></textarea>

              <button type="submit" disabled={status === "loading"}>
                {status === "loading" ? "Submitting..." : "Submit Message"}
              </button>
            </form>
          </div>
        </div>
      </section>
      <Footer />
    </>
  );
}

export default Contact;
